/**
 * PR-27 — Tactical X-Ray safe positioning.
 * Controls where the X-Ray card renders so it never obstructs the pitch.
 * Options: below_radar (default), top_band, side_panel, over_field (legacy).
 */
import { useSyncExternalStore } from "react";
import { trackEvent } from "@/lib/audit";

export type XrayLayout = "below_radar" | "top_band" | "side_panel" | "over_field";

export interface XrayLayoutSettings {
  layout: XrayLayout;
  compact: boolean;
  show_helper_tooltip: boolean;
}

const KEY = "vs:pr27:xray_layout";
const DEFAULTS: XrayLayoutSettings = {
  layout: "below_radar",
  compact: true,
  show_helper_tooltip: true,
};

function read(): XrayLayoutSettings {
  if (typeof window === "undefined") return DEFAULTS;
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : DEFAULTS;
  } catch { return DEFAULTS; }
}

let state: XrayLayoutSettings = read();
const listeners = new Set<() => void>();
function emit() { listeners.forEach((l) => l()); }

export function getXrayLayoutSettings(): XrayLayoutSettings { return state; }

export function updateXrayLayoutSettings(patch: Partial<XrayLayoutSettings>): XrayLayoutSettings {
  const prev = state.layout;
  state = { ...state, ...patch };
  if (typeof window !== "undefined") window.localStorage.setItem(KEY, JSON.stringify(state));
  if (patch.layout && patch.layout !== prev) {
    void trackEvent("tactical_xray_layout_changed", { from: prev, to: patch.layout });
    void trackEvent("tactical_xray_repositioned", { layout: patch.layout });
  }
  emit();
  return state;
}

function subscribe(fn: () => void) { listeners.add(fn); return () => { listeners.delete(fn); }; }
function getSnap() { return state; }

export function useXrayLayoutSettings(): XrayLayoutSettings {
  return useSyncExternalStore(subscribe, getSnap, getSnap);
}